import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Amplify } from 'aws-amplify';
import { I18n } from 'aws-amplify/utils';
import { Authenticator, ThemeProvider, type Theme } from '@aws-amplify/ui-react';
import App from './App';
import { config, isConfigured } from './lib/config';
import '@aws-amplify/ui-react/styles.css';
import './index.css';

if (isConfigured) {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: config.userPoolId!,
        userPoolClientId: config.userPoolClientId!,
      },
    },
  });
}

I18n.putVocabulariesForLanguage('es', {
  // Una cuenta bloqueada desde CRM → Cuentas (o una de Tu equipo sin acceso).
  'User is disabled.': 'Esta cuenta está bloqueada. Si crees que es un error, escribe a Habitania.',
  'Sign In': 'Iniciar sesión',
  'Sign in': 'Iniciar sesión',
  'Signing in': 'Entrando',
  Username: 'Email',
  Password: 'Contraseña',
  'Forgot your password?': '¿Olvidaste tu contraseña?',
  'Reset your password': 'Restablecer contraseña',
  'Send code': 'Enviar código',
  'Confirm Sign In': 'Confirmar inicio de sesión',
  'Change Password': 'Cambiar contraseña',
  'New password': 'Nueva contraseña',
  Confirm: 'Confirmar',
  Submit: 'Enviar',
  Back: 'Volver',
  'Resend Code': 'Reenviar código',
  Code: 'Código',
  'Account recovery requires verified contact information':
    'La recuperación de cuenta necesita un email verificado',
  // Lo que quedaba en inglés en la pantalla de entrada y al recuperar la
  // contraseña (visto al capturar la guía, 26/09). Mismas frases que la web
  // (web/src/portal/amplify.ts): los mensajes de Cognito se emparejan por
  // cadena EXACTA, así que una sin traducir sale en inglés tal cual.
  'Enter your Password': 'Tu contraseña',
  'Enter your Username': 'Tu email',
  'Enter your username': 'Tu email',
  'Enter your Email': 'Tu email',
  'Enter your code': 'Escribe el código',
  'Back to Sign In': 'Volver a entrar',
  'Reset Password': 'Restablecer contraseña',
  'New Password': 'Nueva contraseña',
  'Confirm Password': 'Repite la contraseña',
  'Please confirm your Password': 'Repite la contraseña',
  'Your code is on the way. To log in, enter the code we emailed to':
    'Te hemos enviado un código a',
  'It may take a minute to arrive.': 'Puede tardar un minuto en llegar.',
  'Incorrect username or password.': 'El email o la contraseña no son correctos.',
  'User does not exist.': 'El email o la contraseña no son correctos.',
  'Password did not conform with policy: Password not long enough':
    'La contraseña necesita al menos 10 caracteres.',
  'Invalid verification code provided, please try again.':
    'El código no es correcto. Revísalo o pide uno nuevo.',
  'Username cannot be empty': 'Escribe tu email.',
  'Password cannot be empty': 'Escribe tu contraseña.',
  'Your passwords must match': 'Las dos contraseñas no coinciden.',
  'Attempt limit exceeded, please try after some time.':
    'Demasiados intentos seguidos. Espera unos minutos y vuelve a probar.',
});
I18n.setLanguage('es');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

// Tema Amplify con la paleta Habitania (deep/brand/leaf/soft)
const amplifyTheme: Theme = {
  name: 'habitania-crm',
  tokens: {
    colors: {
      brand: {
        // Cada token es { value }: con el color pelado, Amplify lo ignoraba sin
        // avisar y la pantalla de entrada salía con su azul por defecto.
        primary: {
          '10': { value: '#f1f3e8' },
          '80': { value: '#748922' },
          '90': { value: '#5d6e1b' },
          '100': { value: '#2e3a18' },
        },
      },
      background: {
        primary: { value: '#ffffff' },
        secondary: { value: '#f1f3e8' },
      },
    },
    fonts: {
      default: {
        variable: { value: '"Figtree Variable", system-ui, sans-serif' },
      },
    },
    radii: {
      small: { value: '0.6rem' },
      medium: { value: '0.9rem' },
      large: { value: '1.25rem' },
    },
  },
};

// El botón de «ver la contraseña» de Amplify trae sus textos en inglés («Password
// is hidden», que leen los lectores de pantalla) y no pasan por I18n: van por
// campo. Se ponen en todos los de contraseña.
const ojo = {
  passwordIsHiddenLabel: 'La contraseña está oculta',
  passwordIsShownLabel: 'La contraseña se ve',
  showPasswordButtonLabel: 'Mostrar la contraseña',
};

const formFields = {
  signIn: {
    username: { label: 'Email', placeholder: 'tu@habitania.info' },
    password: { label: 'Contraseña', placeholder: 'Tu contraseña', ...ojo },
  },
  // La política del pool pide 10 caracteres desde el 23/09 (template.yaml,
  // UserPool). Esta pista decía 8: la misma trampa que dejó fuera a Florent
  // en la web. Si se cambia la política, se cambia aquí también.
  forceNewPassword: {
    password: { label: 'Nueva contraseña', placeholder: 'Mínimo 10 caracteres', ...ojo },
    confirm_password: { label: 'Repite la contraseña', ...ojo },
  },
  forgotPassword: { username: { label: 'Email', placeholder: 'tu@habitania.info' } },
  confirmResetPassword: {
    password: { label: 'Nueva contraseña', placeholder: 'Mínimo 10 caracteres', ...ojo },
    confirm_password: { label: 'Confirma la contraseña', ...ojo },
    confirmation_code: { label: 'Código', placeholder: '######' },
  },
};

// La marca encima del formulario de entrada: sin ella, la pantalla parecía de
// otro producto (así lo leyó el lector de prueba de la guía, 26/09).
const componentesAcceso = {
  Header() {
    return (
      <div className="pb-5 pt-2 text-center text-2xl font-extrabold tracking-tight text-white">
        habitania <span className="text-base font-bold tracking-widest text-leaf">CRM</span>
      </div>
    );
  },
};

function Root() {
  if (!isConfigured) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="max-w-md bg-surface border border-deep/10 rounded-2xl px-6 py-8 text-sm">
          <div className="font-bold text-deep">habitania · CRM</div>
          <p className="mt-3 text-ink/60">
            App no configurada. Copia <code>.env.example</code> a{' '}
            <code>.env.local</code> y rellena con los outputs del stack{' '}
            <code>habitania-dev</code>.
          </p>
        </div>
      </div>
    );
  }
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider theme={amplifyTheme}>
          <Authenticator hideSignUp formFields={formFields} components={componentesAcceso}>
            <App />
          </Authenticator>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>,
);
